import React, { useMemo, useState, useCallback } from 'react';
import { OpsTask, TaskStatus, CRMUser } from '../../types';
import OpsTaskCard from './OpsTaskCard';
import './OpsKanban.css';

const STATUS_ORDER: TaskStatus[] = ['pending', 'in-progress', 'review', 'completed', 'blocked', 'on-hold'];
const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Not Started',
  'in-progress': 'In Progress',
  review: 'Review',
  completed: 'Completed',
  blocked: 'Blocked',
  'on-hold': 'On Hold',
};
const STATUS_COLORS: Record<TaskStatus, string> = {
  pending: '#94A3B8',
  'in-progress': '#3B82F6',
  review: '#F59E0B',
  completed: '#22C55E',
  blocked: '#EF4444',
  'on-hold': '#7C3AED',
};

interface OpsKanbanProps {
  tasks: OpsTask[];
  teamMembers: CRMUser[];
  onSelectTask: (task: OpsTask) => void;
  onUpdateTask: (id: string, updates: Partial<OpsTask>) => void;
}

const OpsKanban: React.FC<OpsKanbanProps> = ({ tasks, teamMembers, onSelectTask, onUpdateTask }) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const columns = useMemo(() =>
    STATUS_ORDER.map(status => ({
      id: status,
      label: STATUS_LABELS[status],
      color: STATUS_COLORS[status],
      tasks: tasks.filter(t => t.status === status),
    })),
    [tasks]
  );

  const handleDrop = useCallback((e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    if (!draggedId) return;
    onUpdateTask(draggedId, { status });
    setDraggedId(null);
  }, [draggedId, onUpdateTask]);

  return (
    <div className="ops-kanban pm-scroll">
      {columns.map(col => (
        <div
          key={col.id}
          className="ops-kanban-col"
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDrop={e => handleDrop(e, col.id)}
        >
          <div className="ops-kanban-col-header">
            <span className="ops-kanban-col-dot" style={{ background: col.color }} />
            <span className="ops-kanban-col-label">{col.label}</span>
            <span className="ops-kanban-col-count">{col.tasks.length}</span>
          </div>
          <div className="ops-kanban-col-body pm-scroll">
            {col.tasks.map((task, i) => (
              <div
                key={task.id}
                draggable
                onDragStart={() => setDraggedId(task.id!)}
                onDragEnd={() => setDraggedId(null)}
              >
                <OpsTaskCard
                  task={task}
                  teamMembers={teamMembers}
                  onClick={() => onSelectTask(task)}
                  animDelay={i * 40}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default OpsKanban;
